/**
 * Share Email Types
 *
 * Type definitions for the share email functionality used by BetSlip and other share flows.
 */

/** Chip in a share email item */
export interface ShareEmailChip {
  label: string;
  displayOrder: number;
}

/** Single item in the share email DTO */
export interface ShareEmailBetSlipItem {
  title: string;
  titleColor: string;
  landPageLink: string;
  imageLink: string;
  displayOrder: number;
  chips: ShareEmailChip[];
}

/** The DTO nested inside the share payload */
export interface ShareEmailBetSlipDTO {
  title: string;
  headerSubtitle?: string;
  betSlipLink: string;
  appName: string;
  items: ShareEmailBetSlipItem[];
}

/** Complete share email payload sent to the API */
export interface BetSlipSharePayload {
  template: 'SHARE_BETSLIP_EMAIL';
  recipients: string[];
  accountId: string;
  shareEmailBetSlipDTO: ShareEmailBetSlipDTO;
}

// Success response from API (200)
export interface ShareEmailSuccessData {
  successfulRecipients: string[];
  failedRecipients: string[];
  optedOutRecipients: string[];
}

// Rate limit response from API (429)
export interface ShareEmailRateLimitData {
  reset: string; // ISO timestamp when limit resets
}

// Discriminated union for mutation result handling
export type ShareEmailResult =
  | { status: 'success'; data: ShareEmailSuccessData }
  | { status: 'partial'; data: ShareEmailSuccessData } // Some recipients failed
  | { status: 'rate_limited'; reset: string }
  | { status: 'error'; message: string };

/**
 * Categorize API response into a discriminated union for type-safe handling
 */
export function categorizeShareResult(response: Response, data: unknown): ShareEmailResult {
  if (response.status === 429) {
    const rateLimitData = data as ShareEmailRateLimitData;
    return { status: 'rate_limited', reset: rateLimitData.reset };
  }

  if (!response.ok) {
    return { status: 'error', message: `HTTP ${response.status}: ${response.statusText}` };
  }

  const successData = data as ShareEmailSuccessData;
  const hasFailures = successData.failedRecipients.length > 0 || successData.optedOutRecipients.length > 0;

  return hasFailures ? { status: 'partial', data: successData } : { status: 'success', data: successData };
}

// Email validation regex (from ShareOrder.vue pattern)
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email) && email.length <= 254;
}
